import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getPlanningCredits } from '../../utils/billing';

const TopBar = ({ 
  title, 
  subtitle, 
  onSearch, 
  searchPlaceholder = "Search plans...",
  actions = [],
  breadcrumbs = [],
  onMenuToggle = null,
  onNavigate = null,
  onBillingClick = null,
  onCreditsClick = null
}) => {
  const { user } = useAuth();
  const credits = getPlanningCredits(user);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      // Escape to close search
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  return (
    <header className="sticky top-0 z-[60] bg-white" style={{ borderBottom: '1px solid var(--brand-border)', isolation: 'isolate', willChange: 'transform' }}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Left: Menu button (mobile) and Breadcrumbs or Title */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Mobile Menu Button - Only show on small screens (< 768px) */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                // Force immediate toggle
                const toggleFn = onMenuToggle || window.__toggleSidebar;
                if (toggleFn && typeof toggleFn === 'function') {
                  toggleFn();
                }
              }}
              className="hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition flex-shrink-0 relative z-[70]"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {(!breadcrumbs || breadcrumbs.length === 0) && (
              onNavigate ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate('dashboard');
                  }}
                  className="md:hidden flex-shrink-0 hover:opacity-80 transition-opacity focus:outline-none"
                  title="Go to Dashboard"
                >
                  <img
                    src="/new_icon+text_color.png"
                    alt="CraftMyParty"
                    className="h-10 w-auto object-contain"
                  />
                </button>
              ) : (
                <div className="md:hidden flex-shrink-0">
                  <img
                    src="/new_icon+text_color.png"
                    alt="CraftMyParty"
                    className="h-10 w-auto object-contain"
                  />
                </div>
              )
            )}
            {/* Breadcrumbs or Title */}
            <div className="flex-1 min-w-0">
              {breadcrumbs.length > 0 ? (
                <nav className="flex items-center gap-2 text-sm">
                  {breadcrumbs.map((crumb, idx) => (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                      {crumb.onClick ? (
                        <button
                          onClick={crumb.onClick}
                          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition"
                        >
                          {idx === 0 && (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          )}
                          {idx === 0 && typeof crumb.label === 'string' && crumb.label.startsWith('Back to') ? (
                            <>
                              <span className="hidden sm:inline">{crumb.label}</span>
                              <span className="sm:hidden">Back</span>
                            </>
                          ) : (
                            crumb.label
                          )}
                        </button>
                      ) : (
                        <span className={idx === breadcrumbs.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                          {crumb.label}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              ) : (
                <div>
                  {title && (
                    <h1 className="text-xl font-bold text-gray-900 whitespace-normal break-words">{title}</h1>
                  )}
                  {subtitle && (
                    <p className="text-sm text-gray-500 whitespace-normal break-words">{subtitle}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Center: Search */}
          <div className="flex-1 max-w-md hidden md:block">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="ds-input pl-10 pr-10 py-2 text-sm"
                style={{ borderRadius: '9999px', paddingLeft: '2.5rem' }}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded">
                  ⌘K
                </kbd>
              </div>
            </div>
          </div>

          {/* Mobile Search Button */}
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="md:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {user && (
              <button
                onClick={() => {
                  if (onBillingClick) {
                    onBillingClick();
                  } else if (onCreditsClick) {
                    onCreditsClick();
                  } else if (onNavigate) {
                    onNavigate('billing');
                  } else if (window.location.pathname === '/') {
                    window.location.href = '/?dashboard=true';
                  } else {
                    const navEvent = new CustomEvent('craftmyparty:navigate', { detail: 'billing' });
                    window.dispatchEvent(navEvent);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-full transition text-sm font-medium ${credits === 0 ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200' : 'bg-zinc-50 text-zinc-800 hover:bg-zinc-100 border-zinc-200'}`}
                title="View Billing & Credits"
              >
                {credits === 0 ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )}
                {credits === 0 ? (
                  <span>Add Credits</span>
                ) : (
                  <>
                    <span>{credits}</span>
                    <span className="hidden sm:inline">Credit{credits === 1 ? '' : 's'}</span>
                  </>
                )}
              </button>
            )}
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.onClick}
                className={action.primary ? 'ds-btn-primary px-4 py-2 text-sm' : 'ds-btn-ghost px-4 py-2 text-sm'}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {showSearch && (
          <div className="pb-4 md:hidden">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--brand-muted)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="ds-input pl-10 pr-4 py-2 text-sm"
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default TopBar;
